import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@/components/Button';
import PaymentSection from '@/sections/purchase/PaymentSection';
import FullScreenLoading from '@/components/FullScreenLoading';
import { formatCurrency } from '@/utils/formatUtils';
import { getPaymentCards, getVeritransToken } from '@/api/payment';
import { startPenaltySettlement, getPenaltySettlementStatus, previewPenaltySettlement } from '@/api/penalties';
import usePenaltyStatus from '@/hooks/usePenaltyStatus';
import { VERITRANS_TOKEN_API_KEY } from '@/config/constants';
import { useToast } from '@/components/ToastProvider';
import { usePenaltyPaymentModalState } from '@/stores/penaltyPaymentStore';
import { TooltipProvider } from '@/components/Tooltip';
import closeIcon from '@/assets/close.svg';
import sadIcon from '@/assets/sad.svg';
import useAuthStore from '@/stores/authStore';

const POLLING_INTERVAL = 3000;

const PenaltySettlementModal = () => {
  const { isOpen, close } = usePenaltyPaymentModalState();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const accessToken = useAuthStore((state) => state.accessToken);
  const userId = useAuthStore((state) => state.user?.id);

  const { pendingPenaltyAmount } = usePenaltyStatus();

  const shouldFetchPreview = isOpen && !!accessToken;
  const {
    data: penaltyPreview,
    isLoading: isPenaltyPreviewLoading,
    isFetching: isPenaltyPreviewFetching,
    refetch: refetchPenaltyPreview,
  } = useQuery({
    queryKey: ['penalty-settlement-preview', userId || 'anonymous'],
    queryFn: previewPenaltySettlement,
    enabled: shouldFetchPreview,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    select: (data) => data?.data ?? data ?? null,
  });

  const preview = penaltyPreview?.settlement ?? null;
  const items = penaltyPreview?.items || [];
  const previewToken = penaltyPreview?.token || null;
  const hasPreviewData = Boolean(penaltyPreview);
  const isPenaltyLoading = isPenaltyPreviewLoading || (!hasPreviewData && isPenaltyPreviewFetching);
  const isPenaltyRefreshing = isPenaltyPreviewFetching && hasPreviewData;

  const totalAmount = preview?.totalAmount ?? pendingPenaltyAmount ?? 0;

  const [paymentData, setPaymentData] = useState(null);
  const [isPaymentEditing, setIsPaymentEditing] = useState(false);
  const [selectedPaymentCardId, setSelectedPaymentCardId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const pollingRef = useRef(null);

  const { data: paymentCardsData } = useQuery({
    queryKey: ['payment-cards'],
    queryFn: getPaymentCards,
    enabled: isOpen,
  });

  const paymentCards = useMemo(() => paymentCardsData?.data || [], [paymentCardsData]);

  useEffect(() => {
    if (paymentCards.length > 0 && !selectedPaymentCardId) {
      const defaultCard = paymentCards.find((card) => card.isDefault) || paymentCards[0];
      if (defaultCard) {
        setSelectedPaymentCardId(defaultCard.id);
      }
    }
  }, [paymentCards, selectedPaymentCardId]);

  const clearPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) {
        wasOpenRef.current = true;
        refetchPenaltyPreview();
      }
    } else {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
      }
      clearPolling();
      setPaymentData(null);
      setIsProcessing(false);
      setIsCheckingStatus(false);
      setSelectedPaymentCardId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, clearPolling]);

  const handleSettlementError = useCallback((error) => {
    setIsProcessing(false);
    toast(t('penalty.payment.error'));
  }, [toast, t]);

  const startSettlementMutation = useMutation({
    mutationFn: startPenaltySettlement,
    onError: handleSettlementError,
  });

  const handlePaymentComplete = useCallback((data) => {
    setPaymentData(data);
    if (data.type === 'SAVED_CREDIT_CARD' && data.cardId) {
      setSelectedPaymentCardId(data.cardId);
    }
  }, []);

  const handlePaymentEditingChange = useCallback((isEditing) => {
    setIsPaymentEditing(isEditing);
  }, []);

  const handlePaymentEditRequest = useCallback(() => {
    return !isProcessing && !isCheckingStatus;
  }, [isProcessing, isCheckingStatus]);

  const pollSettlementStatus = useCallback(async (settlementId) => {
    try {
      const response = await getPenaltySettlementStatus(settlementId);
      const status = response.data?.status;

      if (status === 'COMPLETED') {
        clearPolling();
        setIsCheckingStatus(false);
        setIsProcessing(false);
        toast(t('penalty.payment.success'));
        await refetchPenaltyPreview();
        queryClient.invalidateQueries(['payment-cards']);
        queryClient.invalidateQueries(['user-profile']);
        queryClient.invalidateQueries(['sales']);
        close();
        return;
      }

      if (status === 'FAILED' || status === 'EXPIRED') {
        clearPolling();
        setIsCheckingStatus(false);
        setIsProcessing(false);
        if (status === 'FAILED') {
          toast(t('penalty.payment.failed'));
        } else if (status === 'EXPIRED') {
          toast(t('penalty.payment.expired'));
        }
        return;
      }

      pollingRef.current = setTimeout(() => pollSettlementStatus(settlementId), POLLING_INTERVAL);
    } catch (error) {
      console.error('Error checking penalty settlement status:', error);
      pollingRef.current = setTimeout(() => pollSettlementStatus(settlementId), POLLING_INTERVAL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPolling, queryClient, t, close, toast]);

  const handleClose = useCallback(() => {
    if (isProcessing || isCheckingStatus) {
      return;
    }
    close();
  }, [isProcessing, isCheckingStatus, close]);

  const handleSubmit = async () => {
    if (!previewToken || (!items.length && !pendingPenaltyAmount)) {
      toast(t('penalty.payment.no_penalties'));
      return;
    }

    if (!paymentData) {
      toast(t('penalty.payment.select_method'));
      return;
    }

    try {
      setIsProcessing(true);

      let paymentMethodPayload = null;
      if (paymentData.type === 'SAVED_CREDIT_CARD') {
        paymentMethodPayload = {
          type: 'SAVED_CREDIT_CARD',
          cardId: paymentData.cardId,
        };
      } else if (paymentData.type === 'TEMPORARY_CREDIT_CARD') {
        const tokenResponse = await getVeritransToken({
          cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
          cardExpire: paymentData.expiry,
          securityCode: paymentData.cvc,
          cardholderName: paymentData.cardHolder,
          tokenApiKey: VERITRANS_TOKEN_API_KEY,
          lang: 'ja',
        });

        paymentMethodPayload = {
          type: 'TEMPORARY_CREDIT_CARD',
          token: tokenResponse.token,
        };
      }

      if (!paymentMethodPayload) {
        throw new Error('unsupported_payment_method');
      }

      const redirectUri = `${window.location.origin}/payment/done?context=penalty`;

      const response = await startSettlementMutation.mutateAsync({
        previewToken,
        paymentMethod: paymentMethodPayload,
        redirectUri,
      });

      const settlementId = response.data?.id;
      const paymentRedirect = response.data?.payment?.redirectUri;

      if (!settlementId) {
        throw new Error('missing_settlement_id');
      }

      if (paymentRedirect) {
        window.open(paymentRedirect, '_blank');
      }

      setIsCheckingStatus(true);
      pollSettlementStatus(settlementId);
    } catch (error) {
      console.error('Penalty settlement error', error);
      setIsProcessing(false);
      setIsCheckingStatus(false);
      toast(t('penalty.payment.error'));
    }
  };

  if (!isOpen) {
    return null;
  }

  const penaltyItems = items ?? [];
  const hasPenalties = penaltyItems.length > 0 || totalAmount > 0;

  const isActionDisabled =
    isProcessing || isCheckingStatus || isPaymentEditing || !paymentData || isPenaltyRefreshing;

  const renderSummary = () => (
    <div className="space-y-3">
      <div className="flex flex-col items-center text-center gap-2">
        <div className="flex h-11 w-11 items-center justify-center">
          <img src={sadIcon} alt="sad" className="h-full w-full" />
        </div>
        <p className="text-base font-bold text-[#111111]">
          {t('penalty.dialog.penaltyRequired.title')}
        </p>
        <div className="space-y-1 text-sm text-[#666666]">
          <p>{t('penalty.payment.description')}</p>
          <p className="text-xs text-[#aaaaaa]">{t('penalty.payment.notice')}</p>
        </div>
      </div>

      <div className="rounded-md border border-[#f4f4f4]">
        <div className="flex flex-col gap-4 px-6 py-6">
          <div>
            <p className="text-sm font-bold text-[#111111]">
              {t('penalty.payment.summary_title')}
            </p>
          </div>

          {penaltyItems.length > 0 && (
            <div className="space-y-3">
              {penaltyItems.map((item, index) => (
                <div key={index} className="flex items-start justify-between gap-4">
                  <span className="text-sm text-[#111111]">{item.description}</span>
                  <span className="text-sm font-medium text-[#111111]">{formatCurrency(item.penaltyAmount)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-[#f4f4f4] pt-4">
            <span className="text-sm font-bold text-[#111111]">
              {t('penalty.payment.total_label')}
            </span>
            <span className="text-2xl font-bold text-[#111111]">
              {formatCurrency(totalAmount)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (isPenaltyLoading) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-sm text-[#aaaaaa]">
          {t('common.loading')}
        </div>
      );
    }

    if (!hasPenalties) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-sm text-[#666666]">{t('penalty.payment.no_penalties')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-6 px-6 pb-24 pt-6">
        {renderSummary()}
        <div className="overflow-hidden rounded-md border border-[#f4f4f4]">
          <TooltipProvider>
            <PaymentSection
              onComplete={handlePaymentComplete}
              onEditingChange={handlePaymentEditingChange}
              onEditRequest={handlePaymentEditRequest}
              isRequired
              hasPaymentMethods={paymentCards.length > 0}
              paymentCards={paymentCards}
              finalSelectedCardId={selectedPaymentCardId}
              isCompactLayout
            />
          </TooltipProvider>
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    if (isPenaltyLoading) {
      return null;
    }

    if (!hasPenalties) {
      return (
        <div className="px-6 py-4 border-t border-[#f4f4f4]">
          <Button variant="default" size="xl" className="w-full" onClick={handleClose}>
            {t('penalty.payment.back_to_me')}
          </Button>
        </div>
      );
    }

    return (
      <div className="px-6 py-4 border-t border-[#f4f4f4] space-y-2">
        <Button
          variant="primary"
          size="xl"
          className="w-full"
          disabled={isActionDisabled}
          onClick={handleSubmit}
        >
          {isProcessing ? t('common.loading') : t('penalty.payment.pay_action')}
        </Button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60">
      <div className="flex h-full w-[480px] flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f4f4f4] px-6 py-6">
          <h2 className="text-lg font-bold text-[#111111]">{t('penalty.payment.title')}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-6 w-6 items-center justify-center"
            disabled={isProcessing || isCheckingStatus}
          >
            <img src={closeIcon} alt="close" className="h-7 w-7" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>

        {renderFooter()}

        <FullScreenLoading
          isVisible={isCheckingStatus}
          message={t('penalty.payment.verifying')}
        />
      </div>
    </div>
  );
};

export default PenaltySettlementModal;
